/* eslint-disable max-lines */
import {
    Modal,
    ModalContent,
    ModalOverlay,
    useToast,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useCancellationRequestMutation } from './use-cancellation-request-mutation';
import { RequestCancellationFormValues, requestCancellationFormSchema } from './form-schema';
import { RequestCancellationScreen } from './request-screen';
import { ConfirmCancellationScreen } from './confirmation-screen';

export function RequestCancellationModal({
    propertyId,
    isOpen,
    onClose,
}: {
    propertyId: number;
    isOpen: boolean;
    onClose: () => void;
}) {
    const [screen, setScreen] = useState<'REQUEST' | 'CONFIRM'>('REQUEST');

    const toast = useToast();
    const form = useForm<RequestCancellationFormValues>({
        resolver: zodResolver(requestCancellationFormSchema),
        defaultValues: {
            services: [],
        },
    });

    const cancellationMutation = useCancellationRequestMutation(propertyId, {
        onSuccess: () => {
            toast({
                title: 'Cancellation request submitted',
                description: 'Your request has been submitted successfully',
                status: 'success',
            });

            form.reset();
            onClose();
        },
    });

    const handleClose = () => {
        form.reset();
        onClose();
        cancellationMutation.reset();
        setScreen('REQUEST');
    };

    const handleSubmit = () => {
        setScreen('CONFIRM');
    };

    const handleConfirm = (vals: RequestCancellationFormValues) => {
        cancellationMutation.mutate({
            services: vals.services,
            cancellationReasonId: vals.cancellationReasonId,
            notes: vals.notes ?? null,
        });
    };

    const handleBack = () => {
        cancellationMutation.reset();
        setScreen('REQUEST');
    };

    return (
        <Modal
            isOpen={isOpen}
            autoFocus={false}
            closeOnEsc={!cancellationMutation.isPending}
            closeOnOverlayClick={!cancellationMutation.isPending}
            onClose={handleClose}
            size="2xl"
        >
            <ModalOverlay />
            <ModalContent>
                <FormProvider {...form}>
                    {screen === 'REQUEST' ? (
                        <RequestCancellationScreen
                            propertyId={propertyId}
                            onClose={handleClose}
                            onSubmit={handleSubmit}
                        />
                    ) : null}
                    {screen === 'CONFIRM' ? (
                        <ConfirmCancellationScreen
                            propertyId={propertyId}
                            onSubmit={handleConfirm}
                            onBack={handleBack}
                            isLoading={cancellationMutation.isPending}
                            error={cancellationMutation.error}
                        />
                    ) : null}
                </FormProvider>
            </ModalContent>
        </Modal>
    );
}
